import { Button } from "@msuite/ui/button";
import { Checkbox } from "@msuite/ui/checkbox";
import { TextInput } from "@msuite/ui/form";
import { useFilter, useSearchForm } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useQuery } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { useState } from "react";
import { WebfleetObjectCard } from "@/components/cards/webfleet-object-card";
import { client } from "@/hono";

/** Props Interface */
interface ConnectVehicleModalProps extends ModalProps {
	onSelect: (identifier: string) => void;
}

export function ConnectVehicleModal({ isOpen, onClose, onSelect }: ConnectVehicleModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useSearchForm();

	/** State */
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);

	/** Query */
	const { data: resources, refetch } = useQuery({
		queryKey: ["integrations", "webfleet", "resources"],
		queryFn: async () => {
			const response = await client.integrations.webfleet.resources.$get({
				query: { includeIntegrated: "false" },
			});
			const data = await responseHandler(response);
			return data.resources;
		},
	});

	const { filtered } = useFilter(resources ?? [], methods.searchValue);

	/** Functions */
	async function handleOnSubmit() {
		try {
			if (!selectedObjectId) throw new Error("No object selected");
			setIsLoading(true);
			await onSelect(selectedObjectId);
			onClose();
		} catch (error) {
			toast.error(t("general.something_went_wrong"));
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	}

	/** Render */
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={t("vehicles.connect_vehicle_to_webfleet_object")}
		>
			<Modal.Body>
				<TextInput
					isRequired
					label={undefined}
					control={methods.control}
					name="searchValue"
					type="search"
					placeholder={t("placeholders.search")}
				/>
				<div className="overflow-y-scroll v-stack gap-2">
					{filtered?.map((resource) => (
						<div
							className="h-stack gap-4 items-center w-full cursor-pointer"
							key={resource.objectuid}
							onClick={() => {
								if (selectedObjectId === resource.objectuid) {
									setSelectedObjectId(null);
								} else {
									setSelectedObjectId(resource.objectuid);
								}
							}}
						>
							<Checkbox
								value={selectedObjectId === resource.objectuid}
								onChange={() => {}}
							/>
							<WebfleetObjectCard resource={resource} />
						</div>
					))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button
					onClick={onClose}
					variant="ghost"
				>
					{t("general.cancel")}
				</Button>
				<Button
					variant="primary"
					isDisabled={!selectedObjectId}
					onClick={handleOnSubmit}
					isLoading={isLoading}
				>
					{t("general.submit")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
